import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, PlayCircle, Brain, ArrowRight, Sparkles, Moon } from 'lucide-react';
import { ArticleModal } from '../knowledge/ArticleModal';
import { articles } from '../../data/knowledgeContent';

export function SleepKnowledge() {
  const [showArticle, setShowArticle] = useState(false);

  const content = [
    {
      title: "Sleep Stage Science",
      description: "Understanding the different stages of sleep and their importance.",
      type: "article",
      duration: "4 min",
      category: "Science",
      color: "from-primary/20 to-purple-500/20"
    },
    {
      title: "Sleep Optimization",
      description: "Evidence-based strategies for better sleep quality.",
      type: "article",
      duration: "5 min",
      category: "Health",
      color: "from-blue-500/20 to-cyan-500/20"
    },
    {
      title: "Recovery & Sleep",
      description: "How sleep impacts physical and mental recovery.",
      type: "article",
      duration: "6 min",
      category: "Recovery",
      color: "from-purple-500/20 to-pink-500/20"
    }
  ];

  return (
    <div className="theme-card p-6 relative overflow-hidden backdrop-blur-xl border-primary/20">
      {/* Metaverse-inspired Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-purple-400/5 to-background"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_var(--tw-gradient-stops))] from-blue-500/10 via-cyan-500/5 to-transparent"></div>
        <div className="absolute inset-0 backdrop-blur-[100px]"></div>
      </div>
      
      <div className="relative space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Moon className="w-6 h-6 text-primary animate-pulse" />
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
            </div>
            <div>
              <h3 className="text-lg font-semibold bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-400 to-blue-500">
                Sleep Science
              </h3>
              <p className="text-xs text-primary/80">Learn about sleep optimization</p>
            </div>
          </div>
          <button className="relative px-3 py-1.5 rounded-lg group overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex items-center gap-1 text-sm text-primary">
              View All
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </button>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {content.map((item, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => {
                if (item.title === "Sleep Stage Science") {
                  setShowArticle(true);
                }
              }}
              className="group text-left"
            >
              <div className="relative overflow-hidden rounded-lg backdrop-blur-sm border border-primary/10 hover:border-primary/20 transition-all duration-500 h-full">
                {/* Card Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-500" />
                
                {/* Content */}
                <div className="relative p-4 space-y-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                      <BookOpen className="w-5 h-5 text-primary" />
                    </div>
                    <div className="absolute inset-0 bg-primary/20 blur-xl rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex items-center gap-1 text-xs">
                        <Sparkles className="w-3 h-3 text-primary" />
                        <span className="text-primary">{item.category}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {item.duration}
                      </span>
                    </div>
                    <h4 className="font-medium text-sm group-hover:text-primary transition-colors duration-300">
                      {item.title}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Article Modal */}
      {showArticle && (
        <ArticleModal
          title={articles['sleep-stages'].title}
          content={articles['sleep-stages'].content}
          readTime={articles['sleep-stages'].readTime}
          onClose={() => setShowArticle(false)}
        />
      )}
    </div>
  );
}